import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useUsers } from '../../context/AppContext';
import {
    Loader2, Shield, FileText, User, Clock, ChevronDown,
    Search, Download, RefreshCw, AlertCircle, CheckCircle2,
    Crown, UserCog, Eye, Briefcase, Settings, ArrowRight,
    FolderOpen, Zap, Save, Trash2, Edit, Plus
} from 'lucide-react';
import { cn } from '../../utils/cn';

type AuditAction =
    | 'role_change' | 'access_change' | 'user_create' | 'user_delete'
    | 'assignment_create' | 'assignment_delete'
    | 'project_create' | 'project_update' | 'project_delete'
    | 'epic_create' | 'epic_update' | 'epic_delete'
    | 'session_create' | 'session_delete';

interface AuditLog {
    id: string;
    timestamp: string;
    action: AuditAction;
    actorId: string;
    actorEmail?: string;
    targetId: string;
    targetEmail?: string;
    targetType?: 'user' | 'project' | 'epic' | 'assignment' | 'session';
    details: Record<string, any>;
}

// Format relative time
function formatRelativeTime(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Format full date
function formatFullDate(dateString: string) {
    return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

// Action badge component
function ActionBadge({ action }: { action: AuditAction }) {
    const config: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
        // User actions
        role_change: { label: 'Role Change', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400', icon: <Crown size={12} /> },
        access_change: { label: 'Access Change', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400', icon: <Settings size={12} /> },
        user_create: { label: 'User Created', color: 'bg-green-500/10 text-green-600 dark:text-green-400', icon: <Plus size={12} /> },
        user_update: { label: 'User Updated', color: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400', icon: <Edit size={12} /> },
        user_delete: { label: 'User Deleted', color: 'bg-red-500/10 text-red-600 dark:text-red-400', icon: <Trash2 size={12} /> },
        // Assignment actions
        assignment_create: { label: 'Project Assigned', color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400', icon: <Briefcase size={12} /> },
        assignment_delete: { label: 'Project Removed', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400', icon: <Briefcase size={12} /> },
        // Project actions
        project_create: { label: 'Project Created', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', icon: <FolderOpen size={12} /> },
        project_update: { label: 'Project Updated', color: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400', icon: <Edit size={12} /> },
        project_delete: { label: 'Project Deleted', color: 'bg-red-500/10 text-red-600 dark:text-red-400', icon: <FolderOpen size={12} /> },
        // Epic actions
        epic_create: { label: 'Epic Created', color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400', icon: <Zap size={12} /> },
        epic_update: { label: 'Epic Updated', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', icon: <Zap size={12} /> },
        epic_delete: { label: 'Epic Deleted', color: 'bg-rose-500/10 text-rose-600 dark:text-rose-400', icon: <Zap size={12} /> },
        // Session actions
        // Session actions
        session_create: { label: 'Session Saved', color: 'bg-teal-500/10 text-teal-600 dark:text-teal-400', icon: <Save size={12} /> },
        session_delete: { label: 'Session Deleted', color: 'bg-red-500/10 text-red-600 dark:text-red-400', icon: <Trash2 size={12} /> },
    };

    const { label, color, icon } = config[action] || { label: action, color: 'bg-slate-500/10 text-slate-600', icon: null };

    return (
        <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold", color)}>
            {icon}
            {label}
        </span>
    );
}

// Role display helper
function getRoleLabel(role: string): string {
    switch (role) {
        case 'admin': return 'Admin';
        case 'pm': return 'Project Manager';
        case 'executive': return 'Executive';
        case 'owner': return 'Epic Owner';
        case 'normal':
        default: return 'User';
    }
}

// Field label mapping for audit details
const fieldLabels: Record<string, string> = {
    name: 'Name',
    description: 'Description',
    color: 'Color',
    status: 'Status',
    priority: 'Priority',
    title: 'Title',
    notes: 'Notes',
    so: 'Service Owner',
    arq: 'Architecture',
    security: 'Security Score',
    business: 'Business Score',
    time: 'Time Criticality',
    ops: 'Ops Enablement',
    confidence: 'Confidence',
    effort: 'Effort',
    lane: 'Lane',
    projectId: 'Project',
    ownerId: 'Owner',
    displayName: 'Display Name'
};

export default function AdminAuditPage() {
    const api = useApi();
    const { isAdmin } = useAuth();
    const { users } = useUsers();

    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [actionFilter, setActionFilter] = useState('all');
    const [expandedLog, setExpandedLog] = useState<string | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;

    // Reset page on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, actionFilter]);

    useEffect(() => {
        if (isAdmin) {
            loadLogs();
        }
    }, [isAdmin]);

    const loadLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await (api as any).getAuditLogs?.() || [];
            setLogs(data);
        } catch (err) {
            console.error('Failed to load audit logs:', err);
            setError('Failed to load audit logs. The endpoint may not be available yet.');
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    // Helper to resolve user name
    const getUserName = (id: string, email?: string) => {
        const user = users.find(u => u.id === id || u.email === email);
        if (user && user.displayName) return user.displayName;
        return email?.split('@')[0] || id.substring(0, 8);
    };

    const getUserEmail = (id: string, email?: string) => {
        const user = users.find(u => u.id === id || u.email === email);
        if (user && user.email) return user.email;
        return email || id;
    };

    // Filtered logs
    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            const matchesSearch = !searchQuery ||
                log.actorEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                log.targetEmail?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesAction = actionFilter === 'all' || log.action === actionFilter;
            return matchesSearch && matchesAction;
        });
    }, [logs, searchQuery, actionFilter]);

    // Export to CSV
    const exportToCSV = () => {
        const headers = ['Timestamp', 'Action', 'Actor', 'Target', 'Details'];
        const rows = filteredLogs.map(log => [
            formatFullDate(log.timestamp),
            log.action,
            log.actorEmail || log.actorId,
            log.targetEmail || log.targetId,
            JSON.stringify(log.details)
        ]);

        const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Render log details
    const renderDetails = (log: AuditLog) => {
        if (log.action === 'role_change') {
            return (
                <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                        {getRoleLabel(log.details.previousRole)}
                    </span>
                    <ArrowRight size={14} className="text-siemens-text-soft" />
                    <span className="px-2 py-0.5 rounded bg-siemens-petrol/10 text-siemens-petrol font-medium">
                        {getRoleLabel(log.details.newRole)}
                    </span>
                </div>
            );
        }

        if (log.action === 'access_change') {
            const prev = Object.entries(log.details.previousAccess || {}).filter(([, v]) => v).map(([k]) => k);
            const next = Object.entries(log.details.newAccess || {}).filter(([, v]) => v).map(([k]) => k);
            return (
                <div className="text-sm">
                    <div className="flex items-center gap-2">
                        <span className="text-siemens-text-soft">{prev.length}/7 pages</span>
                        <ArrowRight size={14} className="text-siemens-text-soft" />
                        <span className="font-medium text-siemens-petrol">{next.length}/7 pages</span>
                    </div>
                    {expandedLog === log.id && (
                        <div className="mt-2 text-xs text-siemens-text-soft">
                            <div><strong>Before:</strong> {prev.join(', ') || 'None'}</div>
                            <div><strong>After:</strong> {next.join(', ') || 'None'}</div>
                        </div>
                    )}
                </div>
            );
        }

        if (log.action === 'user_update') {
            const changes = log.details.changes || {};
            return (
                <div className="text-sm">
                    <span className="font-medium text-siemens-deep-blue dark:text-white">Profile Updated</span>
                    {Object.keys(changes).length > 0 && (
                        <div className="mt-1 text-xs text-siemens-text-soft space-y-0.5">
                            {Object.entries(changes).map(([key, val]: [string, any]) => (
                                <div key={key} className="flex gap-1">
                                    <span className="font-semibold text-siemens-deep-blue dark:text-siemens-elevation-text-strong">
                                        {fieldLabels[key] || key}:
                                    </span>
                                    <span className="truncate max-w-[200px]" title={`${val.from} → ${val.to}`}>
                                        {String(val.from)} → {String(val.to)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        // Project actions
        if (log.action === 'project_create' || log.action === 'project_delete') {
            return (
                <div className="text-sm">
                    <span className="font-medium">{log.details.name || log.details.projectName}</span>
                </div>
            );
        }

        if (log.action === 'project_update') {
            const changes = log.details.changes || {};
            return (
                <div className="text-sm">
                    <span className="font-medium">{log.details.projectName || log.details.name}</span>
                    {Object.keys(changes).length > 0 && (
                        <div className="mt-1 text-xs text-siemens-text-soft space-y-0.5">
                            {Object.entries(changes).map(([key, val]: [string, any]) => {
                                // Special handling for PM array
                                if (key === 'pm') {
                                    const from = Array.isArray(val.from) ? val.from.map((id: string) => getUserName(id)).join(', ') : '';
                                    const to = Array.isArray(val.to) ? val.to.map((id: string) => getUserName(id)).join(', ') : '';
                                    return (
                                        <div key={key} className="flex gap-1">
                                            <span className="font-semibold text-siemens-deep-blue dark:text-siemens-elevation-text-strong">
                                                {fieldLabels[key] || key}:
                                            </span>
                                            <span className="truncate max-w-[200px]" title={`${from} → ${to}`}>
                                                {from || 'None'} → {to || 'None'}
                                            </span>
                                        </div>
                                    );
                                }

                                return (
                                    <div key={key} className="flex gap-1">
                                        <span className="font-semibold text-siemens-deep-blue dark:text-siemens-elevation-text-strong">
                                            {fieldLabels[key] || key}:
                                        </span>
                                        <span className="truncate max-w-[200px]" title={`${val.from} → ${val.to}`}>
                                            {String(val.from)} → {String(val.to)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        // Epic actions
        if (log.action === 'epic_create') {
            return (
                <div className="text-sm">
                    <span className="font-medium">{log.details.title}</span>
                    <span className="text-siemens-text-soft ml-2">({log.details.lane})</span>
                </div>
            );
        }

        if (log.action === 'epic_update') {
            const changes = log.details.changes || {};
            return (
                <div className="text-sm">
                    <span className="font-medium">{log.details.epicTitle}</span>
                    {Object.keys(changes).length > 0 && (
                        <div className="mt-1 text-xs text-siemens-text-soft space-y-0.5">
                            {Object.entries(changes).map(([key, val]: [string, any]) => (
                                <div key={key} className="flex gap-1">
                                    <span className="font-semibold text-siemens-deep-blue dark:text-siemens-elevation-text-strong">
                                        {fieldLabels[key] || key}:
                                    </span>
                                    <span className="truncate max-w-[200px]" title={`${val.from} → ${val.to}`}>
                                        {String(val.from)} → {String(val.to)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        if (log.action === 'epic_delete') {
            return <span className="text-sm text-siemens-text-soft">Epic removed</span>;
        }

        // Assignment actions
        if (log.action === 'assignment_create' || log.action === 'assignment_delete') {
            return (
                <div className="text-sm text-siemens-text-soft">
                    Project: {log.details.projectName || log.details.projectId?.substring(0, 12)}
                </div>
            );
        }

        // Session actions
        if (log.action === 'session_create') {
            return (
                <div className="text-sm">
                    <span className="font-medium">{log.details.label}</span>
                    <span className="text-siemens-text-soft ml-2">({log.details.epicCount} epics)</span>
                </div>
            );
        }

        if (log.action === 'session_delete') {
            return (
                <div className="text-sm text-siemens-text-soft">
                    Session "{log.details.label}" removed
                </div>
            );
        }

        return (
            <pre className="text-xs text-siemens-text-soft overflow-x-auto">
                {JSON.stringify(log.details, null, 2)}
            </pre>
        );
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-96 gap-4">
                <Loader2 className="w-8 h-8 text-siemens-petrol animate-spin" />
                <p className="text-sm text-siemens-text-soft">Loading audit logs...</p>
            </div>
        );
    }

    const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
    const paginatedLogs = filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="h-full overflow-y-auto">
            <div className="p-6 max-w-7xl mx-auto space-y-6">
                {/* Page Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-siemens-deep-blue dark:text-white flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-siemens-petrol/10 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-siemens-petrol" />
                            </div>
                            Audit Logs
                        </h1>
                        <p className="text-sm text-siemens-text-soft mt-1">Track all role and permission changes</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={loadLogs}
                            className="btn-siemens-secondary flex items-center gap-2"
                        >
                            <RefreshCw size={16} />
                            Refresh
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={exportToCSV}
                            disabled={filteredLogs.length === 0}
                            className="btn-siemens-primary flex items-center gap-2 disabled:opacity-50"
                        >
                            <Download size={16} />
                            Export CSV
                        </motion.button>
                    </div>
                </div>

                {/* Error State */}
                {error && (
                    <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 p-4 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        <p className="text-sm text-amber-800 dark:text-amber-300">{error}</p>
                    </div>
                )}

                {/* Search & Filter */}
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-siemens-text-soft" />
                        <input
                            type="text"
                            placeholder="Search by email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="siemens-input pl-10"
                        />
                    </div>
                    <div className="relative">
                        <select
                            value={actionFilter}
                            onChange={(e) => setActionFilter(e.target.value)}
                            className="siemens-input pr-10 appearance-none cursor-pointer min-w-[180px]"
                        >
                            <option value="all">All Actions</option>
                            <optgroup label="User">
                                <option value="role_change">Role Changes</option>
                                <option value="access_change">Access Changes</option>
                                <option value="user_create">User Created</option>
                                <option value="user_delete">User Deleted</option>
                            </optgroup>
                            <optgroup label="Assignments">
                                <option value="assignment_create">Project Assigned</option>
                                <option value="assignment_delete">Project Removed</option>
                            </optgroup>
                            <optgroup label="Projects">
                                <option value="project_create">Project Created</option>
                                <option value="project_update">Project Updated</option>
                                <option value="project_delete">Project Deleted</option>
                            </optgroup>
                            <optgroup label="Epics">
                                <option value="epic_create">Epic Created</option>
                                <option value="epic_update">Epic Updated</option>
                                <option value="epic_delete">Epic Deleted</option>
                            </optgroup>
                            <optgroup label="Sessions">
                                <option value="session_create">Session Saved</option>
                                <option value="session_delete">Session Deleted</option>
                            </optgroup>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-siemens-text-soft pointer-events-none" />
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider">
                        <div className="text-2xl font-bold text-siemens-deep-blue dark:text-white">{logs.length}</div>
                        <div className="text-sm text-siemens-text-soft">Total Logs</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider">
                        <div className="text-2xl font-bold text-purple-600">{logs.filter(l => l.action === 'role_change').length}</div>
                        <div className="text-sm text-siemens-text-soft">Role Changes</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider">
                        <div className="text-2xl font-bold text-blue-600">{logs.filter(l => l.action === 'access_change').length}</div>
                        <div className="text-sm text-siemens-text-soft">Access Changes</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider">
                        <div className="text-2xl font-bold text-siemens-petrol">{filteredLogs.length}</div>
                        <div className="text-sm text-siemens-text-soft">Showing</div>
                    </div>
                </div>

                {/* Logs Table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider overflow-hidden"
                >
                    {filteredLogs.length === 0 ? (
                        <div className="p-12 text-center">
                            <FileText className="w-12 h-12 text-siemens-text-soft mx-auto mb-4 opacity-50" />
                            <h3 className="text-lg font-semibold text-siemens-deep-blue dark:text-white mb-2">No audit logs found</h3>
                            <p className="text-sm text-siemens-text-soft">
                                {searchQuery || actionFilter !== 'all'
                                    ? 'Try adjusting your search or filters'
                                    : 'Role and permission changes will appear here'}
                            </p>
                        </div>
                    ) : (
                        <table className="siemens-table">
                            <thead>
                                <tr>
                                    <th className="w-[15%]">Timestamp</th>
                                    <th className="w-[15%]">Action</th>
                                    <th className="w-[20%]">Actor</th>
                                    <th className="w-[20%]">Target</th>
                                    <th className="w-[30%]">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence mode='wait'>
                                    {paginatedLogs.map((log, idx) => (
                                        <motion.tr
                                            key={log.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ delay: idx * 0.02 }}
                                            onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                                            className="cursor-pointer hover:bg-siemens-deep-blue-8/50 dark:hover:bg-siemens-elevation-selected/50 transition-colors"
                                        >
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <Clock size={14} className="text-siemens-text-soft" />
                                                    <span className="text-sm" title={formatFullDate(log.timestamp)}>
                                                        {formatRelativeTime(log.timestamp)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <ActionBadge action={log.action} />
                                            </td>
                                            <td>
                                                <div className="text-sm font-medium text-siemens-deep-blue dark:text-white">
                                                    {getUserName(log.actorId, log.actorEmail)}
                                                </div>
                                                <div className="text-xs text-siemens-text-soft truncate max-w-[150px]">
                                                    {getUserEmail(log.actorId, log.actorEmail)}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="text-sm font-medium text-siemens-deep-blue dark:text-white">
                                                    {(() => {
                                                        if (log.targetType === 'user') return getUserName(log.targetId, log.targetEmail);

                                                        // For Projects and Epics, try to find name in details first
                                                        if (log.targetType === 'project') return log.details.projectName || log.details.name || log.targetId;
                                                        if (log.targetType === 'epic') return log.details.epicTitle || log.details.title || log.targetId;
                                                        if (log.targetType === 'session') return log.details.label || log.targetId;
                                                        if (log.targetType === 'assignment') return log.details.projectName || log.details.project || log.targetId;

                                                        // Fallback
                                                        return log.targetEmail?.split('@')[0] || log.targetId;
                                                    })()}
                                                </div>
                                                <div className="text-xs text-siemens-text-soft truncate max-w-[150px]">
                                                    {log.targetType === 'user' ? getUserEmail(log.targetId, log.targetEmail) : (log.targetEmail || log.targetId)}
                                                </div>
                                            </td>

                                            <td>
                                                {renderDetails(log)}
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    )}
                </motion.div>
            </div >
            {/* Pagination Controls */}
            {
                filteredLogs.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-siemens-elevation-base-1 rounded-lg border border-siemens-deep-blue/8 dark:border-siemens-divider">
                        <div className="text-sm text-siemens-text-soft">
                            Showing <span className="font-semibold text-siemens-deep-blue dark:text-white">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-semibold text-siemens-deep-blue dark:text-white">{Math.min(currentPage * itemsPerPage, filteredLogs.length)}</span> of <span className="font-semibold text-siemens-deep-blue dark:text-white">{filteredLogs.length}</span> logs
                        </div>
                        {totalPages > 1 && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Previous
                                </button>
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pNum = i + 1;
                                        if (totalPages > 5 && currentPage > 3) {
                                            pNum = currentPage - 2 + i;
                                        }
                                        if (pNum > totalPages) return null;

                                        return (
                                            <button
                                                key={pNum}
                                                onClick={() => setCurrentPage(pNum)}
                                                className={cn(
                                                    "w-8 h-8 rounded-lg text-sm font-medium transition-colors",
                                                    currentPage === pNum
                                                        ? "bg-siemens-petrol text-white"
                                                        : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                                                )}
                                            >
                                                {pNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                )
            }
        </div >
    );
}
